import React, { useEffect, useMemo, useState } from "react";
import {
  ref,
  onValue,
  getDatabase,
  update,
  query,
  orderByChild,
  equalTo,
  get,
  runTransaction,
  remove,
} from "firebase/database";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  updateDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  COLLECTION_NAME,
  SEND_NOTIFICATION,
  REFUND_REQUEST,
  CANCEL_RIDE,
  UPDATE_RIDE,
  RideStatus,
} from "../../constants/endpoints";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, Menu } from "antd";
import { Bounce, toast } from "react-toastify";

import Table from "../../Components/Table/Table";
import Loader from "../../Components/Loader/Loader";
import AssignDriverModal from "../../Components/AssignDriverModal/AssignDriverModal";
import ShowMoreText from "../../Components/ShowMoreText/ShowMoreText";
import { useAuth } from "../../context/AuthContext";
import { firebaseTimeToDate, firebaseTimeToTimestamp } from "../../utils";

const capitalizeFirstLetter = (string) => {
  if (!string) return "-----";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const ActiveRides = () => {
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedRole, setSelectedRole] = useState("awaiting-rides");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRide, setSelectedRide] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const { currentUser } = useAuth();

  const fetchUserDetails = async (userId) => {
    if (!userId) return { name: "Unknown", phone: "-----" };
    try {
      const userDoc = await getDoc(doc(db, COLLECTION_NAME.users, userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return {
          name: userData.fullName || "Unknown",
          phone: userData.phoneNumber || "-----",
        };
      }
      return { name: "Unknown", phone: "-----" };
    } catch (error) {
      console.error(`Error fetching user ${userId}:`, error);
      return { name: "Unknown", phone: "-----" };
    }
  };

  const fetchScheduledRides = async () => {
    try {
      const scheduledRidesRef = collection(db, COLLECTION_NAME.scheduleRides);
      const snapshot = await getDocs(scheduledRidesRef);

      const scheduledRides = [];
      snapshot.forEach((doc) => {
        const rideData = doc.data();

        const dateObj = firebaseTimeToDate(rideData.createdAt);
        const createdAtTimestamp = firebaseTimeToTimestamp(rideData.createdAt);

        scheduledRides.push({
          dbKey: doc.id,
          ...rideData,
          createdAt: dateObj,
          createdAtTimestamp: createdAtTimestamp,
          pickupTime: rideData.pickupTime
            ? new Date(rideData.pickupTime)
            : null,
          source: "firestore", // Add source identifier
        });
      });

      return scheduledRides;
    } catch (error) {
      console.error("Error fetching scheduled rides:", error);
      return [];
    }
  };

  useEffect(() => {
    const database = getDatabase();
    const ridesRef = ref(database, COLLECTION_NAME.rides);

    setLoading(true);

    const unsubscribe = onValue(ridesRef, async (snapshot) => {
      const ridesData = snapshot.val();
      let realtimeRides = [];

      if (ridesData) {
        realtimeRides = Object.keys(ridesData).map((key) => {
          const ride = ridesData[key];

          const dateObj = firebaseTimeToDate(ride.createdAt);
          const createdAtTimestamp = firebaseTimeToTimestamp(ride.createdAt);

          return {
            dbKey: key,
            ...ride,
            createdAt: dateObj,
            createdAtTimestamp: createdAtTimestamp,
            pickupTime: ride.pickupTime ? new Date(ride.pickupTime) : null,
            source: "realtime", // Add source identifier
          };
        });
      }

      // Fetch scheduled rides from Firestore if awaiting rides is selected
      let scheduledRides = [];
      if (selectedRole === "awaiting-rides") {
        scheduledRides = await fetchScheduledRides();
      }

      // Combine both data sources
      const allRides = [...realtimeRides, ...scheduledRides];

      // Filter by role
      const filteredByRole = allRides.filter((ride) => {
        if (selectedRole === "in-progress") {
          return (
            !!ride.riderId &&
            ride.socketStatus !== "completed" &&
            ride.status !== "pending" &&
            ride?.socketStatus !== "driverPickedScheduled"
          );
        } else if (selectedRole === "awaiting-rides") {
          return (
            ride.status === "pending" ||
            ride?.socketStatus === "driverPickedScheduled"
          );
        }
        return true;
      });

      // Enrich with passenger + driver names and phone numbers
      const completedRides = await Promise.all(
        filteredByRole.map(async (ride) => {
          const passengerDetails = await fetchUserDetails(ride.userId);
          const driverDetails = ride.riderId
            ? await fetchUserDetails(ride.riderId)
            : { name: "Not Assigned", phone: "-----" };

          return {
            ...ride,
            passengerName: passengerDetails.name,
            passengerPhone: passengerDetails.phone,
            driverName: driverDetails.name,
            driverPhone: driverDetails.phone,
          };
        })
      );

      // Sort
      const sortedData = completedRides.sort((a, b) => {
        // Handle invalid timestamps
        const aTime = isNaN(a.createdAtTimestamp) ? 0 : a.createdAtTimestamp;
        const bTime = isNaN(b.createdAtTimestamp) ? 0 : b.createdAtTimestamp;

        return sortOrder === "desc" ? bTime - aTime : aTime - bTime;
      });

      setData(sortedData);
      setLoading(false);
    });

    // Cleanup listener on unmount / dependency change
    return () => unsubscribe();
  }, [sortOrder, selectedRole]);

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
  };

  const isAwaitingRides = selectedRole === "awaiting-rides";
  const isInProgress = selectedRole === "in-progress";

  const cancelRide = async (rideId, refundReason) => {
    try {
      const cancelRideData = {
        userId: currentUser?.uid,
        ride_id: rideId,
        refund_reason: refundReason,
      };

      console.log("cancelRideData", cancelRideData);

      const response = await fetch(CANCEL_RIDE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cancelRideData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Cancel ride request failed:", errorText);
        throw new Error(errorText || "Failed to cancel ride");
      }

      const cancelData = await response.json();
      console.log("CANCEL_RIDE===>response", cancelData);
      return cancelData;
    } catch (error) {
      console.error("Error calling cancel ride API:", error);
      throw error;
    }
  };

  const updateRide = async (rideId, driverId, updateData) => {
    try {
      const updateRideData = {
        rideId: rideId,
        driverId: driverId,
        updates: {
          ...updateData,
          rideOrigin: "admin",
        },
      };

      console.log("updateRideData", updateRideData);

      const response = await fetch(UPDATE_RIDE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateRideData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Update ride request failed:", errorText);
        throw new Error(errorText || "Failed to update ride");
      }

      const responseData = await response.json();
      console.log("UPDATE_RIDE===>response", responseData);
      return responseData;
    } catch (error) {
      console.error("Error calling update ride API:", error);
      throw error;
    }
  };

  const getUserToken = async (userId) => {
    try {
      const userDocRef = doc(db, COLLECTION_NAME.users, userId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData?.token || null;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user token:", error);
      return null;
    }
  };

  const sendPassengerNotification = async (userId, title, body) => {
    try {
      const token = await getUserToken(userId);

      if (!token) {
        console.log("User token not found, skipping notification");
        return;
      }

      const response = await fetch(SEND_NOTIFICATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: title,
          body: body,
          token: token,
        }),
      });

      if (response.ok) {
        console.log("Notification sent successfully to passenger");
      } else {
        console.error("Failed to send notification:", await response.text());
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const markRideCompleted = async (rideData) => {
    try {
      // Get passenger data
      const passengerDocRef = doc(db, COLLECTION_NAME.users, rideData?.userId);
      const passengerDoc = await getDoc(passengerDocRef);
      const passenger = passengerDoc.exists() ? passengerDoc.data() : null;

      // Get driver data from rideData (riderId)
      let driver = null;
      if (rideData?.riderId) {
        const driverDocRef = doc(db, COLLECTION_NAME.users, rideData.riderId);
        const driverDoc = await getDoc(driverDocRef);
        driver = driverDoc.exists() ? driverDoc.data() : null;
      }

      // Use riderName from rideData if available, otherwise get from driver document
      const riderName = rideData?.riderName || driver?.fullName || "Admin";
      const riderId = rideData?.riderId || currentUser?.uid;

      // Save to ridesHistory
      await setDoc(
        doc(db, COLLECTION_NAME.ridesHistory, rideData?.id),
        {
          ...rideData,
          status: "completed",
          socketStatus: "completed",
          riderName: riderName,
          riderId: riderId,
          passengerName: passenger?.fullName || "Unknown",
          driverRating: true,
        },
        { merge: false }
      );

      // Update ride in realtime database
      const database = getDatabase();
      const ridesRef = ref(database, COLLECTION_NAME.rides);
      const rideQuery = query(
        ridesRef,
        orderByChild("id"),
        equalTo(rideData?.id)
      );
      const snapshot = await get(rideQuery);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const key = Object.keys(data)[0];
        const rideRef = ref(database, `${COLLECTION_NAME.rides}/${key}`);

        await update(rideRef, {
          status: "completed",
          socketStatus: "completed",
          riderName: riderName,
          riderId: riderId,
          passengerName: passenger?.fullName || "Unknown",
          driverRating: true,
        });
      }

      // Update driver availability status
      if (rideData?.riderId) {
        try {
          const driverUserDocRef = doc(
            db,
            COLLECTION_NAME.users,
            rideData.riderId
          );
          const driverUserDoc = await getDoc(driverUserDocRef);

          if (driverUserDoc.exists()) {
            const userData = driverUserDoc.data();
            if (userData.role === "Rider") {
              await updateDoc(driverUserDocRef, {
                availabilityStatus: "available",
              });
              console.log("Driver availability status updated to available");
            }
          }
        } catch (error) {
          console.error("Error updating driver availability:", error);
        }
      }

      return { success: true };
    } catch (err) {
      console.error("Error while marking ride completed:", err);
      return { success: false, error: err };
    }
  };

  const handleCompleteRide = async (ride) => {
    try {
      setIsAssigning(true);

      const database = getDatabase();
      const ridesRef = ref(database, COLLECTION_NAME.rides);
      const rideQuery = query(ridesRef, orderByChild("id"), equalTo(ride.id));
      const snapshot = await get(rideQuery);

      if (!snapshot.exists()) {
        toast.error("Ride not found in database.", {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        });
        return;
      }

      const data = snapshot.val();
      const key = Object.keys(data)[0];
      const rideDataFromDb = data[key];
      const response = await markRideCompleted(rideDataFromDb);

      if (!response?.success) {
        throw new Error("Failed to mark ride as completed");
      }

      await sendPassengerNotification(
        rideDataFromDb?.userId,
        "Ride Completed",
        "Your ride has been completed."
      );

      toast.success("Ride has been completed successfully.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } catch (error) {
      console.error("Error while completing ride:", error);
      toast.error("Failed to complete ride. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: "Ride ID",
        accessor: "id",
        Cell: ({ value }) => value || "-----",
      },
      {
        Header: "Passenger Name",
        accessor: "passengerName",
        Cell: ({ value }) => value || "-----",
      },
      {
        Header: "Passenger Phone",
        accessor: "passengerPhone",
        Cell: ({ value }) => value || "-----",
      },
      {
        Header: "Driver Name",
        accessor: "driverName",
        Cell: ({ value }) => value || "Not Assigned",
      },
      {
        Header: "Driver Phone",
        accessor: "driverPhone",
        Cell: ({ value }) => value || "-----",
      },
      {
        Header: "Passengers",
        accessor: "numberOfPassenger",
        Cell: ({ value }) => value || "-----",
      },
      {
        Header: "Scheduled",
        accessor: "isScheduled",
        Cell: ({ row }) => {
          const { isScheduled, scheduledTime } = row.original;

          if (!isScheduled) {
            return (
              <span
                className="px-2 py-1 rounded-full text-xs font-medium bg-celadon text-primary
            "
              >
                Normal Ride
              </span>
            );
          }

          if (!scheduledTime) return "-----";

          const dateObj = new Date(scheduledTime);

          const formatted = dateObj.toLocaleString("en-US", {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-celadon text-primary">
              {formatted}
            </span>
          );
        },
      },
      {
        Header: "Destination Address",
        accessor: "destinationAddress",
        Cell: ({ value }) => <ShowMoreText text={value || ""} />,
      },
      {
        Header: "Destination Zone",
        accessor: "destinationZone",
        Cell: ({ value }) => <ShowMoreText text={value || ""} />,
      },
      {
        Header: "Pickup Address",
        accessor: "pickupAddress",
        Cell: ({ value }) => <ShowMoreText text={value || ""} />,
      },
      {
        Header: "Pickup Zone",
        accessor: "pickupZone",
        Cell: ({ value }) => <ShowMoreText text={value || ""} />,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => capitalizeFirstLetter(value),
      },
      {
        Header: (
          <div
            onClick={toggleSortOrder}
            className="cursor-pointer flex gap-1 items-center"
          >
            Created At{" "}
            {sortOrder === "desc" ? <IoIosArrowDown /> : <IoIosArrowUp />}
          </div>
        ),
        accessor: "createdAt",
        Cell: ({ value }) => {
          if (!value) return "-----";

          const formatted = value.toLocaleString("en-US", {
            weekday: "short",
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });

          return (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-celadon text-primary">
              {formatted}
            </span>
          );
        },
      },
    ];

    // Conditionally add the Assign Driver column
    if (isAwaitingRides) {
      baseColumns.push({
        Header: "Assign Driver",
        accessor: "assign",
        Cell: ({ row }) => {
          // Always show assign button for awaiting rides
          return (
            <button
              className="bg-celadon text-primary px-4 py-2 rounded-md"
              onClick={() => openModal(row.original)}
            >
              Assign Driver
            </button>
          );
        },
      });
    }

    // Conditionally add the Status Actions column for in-progress rides
    if (isInProgress) {
      baseColumns.push({
        Header: "Emergency Actions",
        accessor: "statusActions",
        Cell: ({ row }) => {
          const ride = row.original;
          // Only enable "Driver Picked Up" and "Passenger No Show" when socketStatus is "driverPickup"
          // Disable for "Arrived" and all other socketStatus values
          const isDriverPickedUpDisabled =
            ride?.socketStatus !== "driverPickup";

          const menu = (
            <Menu>
              <Menu.Item
                key="passengerNoShow"
                onClick={() =>
                  cancelRide(ride?.id, RideStatus.PASSENGER_NO_SHOW)
                }
                disabled={isDriverPickedUpDisabled}
              >
                Passenger No Show (No refund available)
              </Menu.Item>
              <Menu.Item
                key="pickedUp"
                onClick={() =>
                  updateRide(ride?.id, ride?.riderId, {
                    arrived: true,
                    socketStatus: "Arrived",
                  })
                }
                disabled={isDriverPickedUpDisabled}
              >
                Driver Picked Up (Ride status will be updated)
              </Menu.Item>
              <Menu.Item
                key="cancelDriver"
                onClick={() => cancelRide(ride?.id, RideStatus.MID_WAY_DRIVER)}
              >
                Cancel by Driver Midway (50% refund available)
              </Menu.Item>
              <Menu.Item
                key="cancelPassenger"
                onClick={() =>
                  cancelRide(ride?.id, RideStatus.MID_WAY_PASSENGER)
                }
              >
                Cancel by Passenger Midway (No refund available)
              </Menu.Item>
              <Menu.Item
                key="complete"
                onClick={() => handleCompleteRide(ride)}
              >
                Complete Ride (No refund available)
              </Menu.Item>
            </Menu>
          );

          return (
            <div className="flex items-center justify-center w-full">
              <Dropdown overlay={menu} trigger={["click"]}>
                <button className="bg-celadon text-primary text-sm font-medium flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors">
                  <span>Manage Ride</span>
                </button>
              </Dropdown>
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [sortOrder, isAwaitingRides, isInProgress]);

  const onVerify = async (driver, ride) => {
    try {
      setIsAssigning(true);
      console.log("Assigning driver:", driver);
      console.log("Ride data:", ride);
      console.log("Ride source:", ride?.source);

      // const newSocketStatus = ride?.scoc
      //   ride?.isScheduled &&
      //   (ride?.status === "pending" || ride?.status === "scheduled")
      //     ? "rideCreated"
      //     : "driverPickup";

      const newSocketStatus = ride?.socketStatus;

      const updateData = {
        rideStartedTime: new Date().toLocaleString(),
        riderId: driver.id,
        riderName: driver.fullName,
        socketStatus: newSocketStatus,
      };

      // If ride is pending, existing socketStatus is rideCreated, and it's not a scheduled ride, change status to accepted
      if (
        ride?.status === "pending" &&
        ride?.socketStatus === "rideCreated" &&
        !ride?.isScheduled
      ) {
        updateData.status = "pending";
      }

      // Check ride source and update accordingly
      if (ride.source === "firestore") {
        // Update Firestore document
        console.log("Updating Firestore document:", ride.dbKey);
        const firestoreDocRef = doc(
          db,
          COLLECTION_NAME.scheduleRides,
          ride.dbKey
        );
        await updateDoc(firestoreDocRef, updateData);
        console.log("Firestore update successful");
      } else {
        // Update Realtime Database
        console.log("Updating Realtime Database:", ride.dbKey);
        const database = getDatabase();
        const rideRef = ref(database, `${COLLECTION_NAME.rides}/${ride.dbKey}`);
        await update(rideRef, updateData);
        console.log("Realtime Database update successful");
      }

      toast.success(
        `Driver ${driver.fullName} assigned to ride successfully!`,
        {
          position: "top-right",
          autoClose: 5000,
          transition: Bounce,
        }
      );

      closeModal();
    } catch (error) {
      console.error("Error assigning driver:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
      });
      toast.error("Failed to assign driver. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRoleChange = (role) => {
    setSelectedRole(role);
  };

  const closeModal = () => {
    setSelectedRide(null);
    setIsModalOpen(false);
  };

  const openModal = (ride) => {
    setIsModalOpen(true);
    setSelectedRide(ride);
  };

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-2 md:mb-5 text-primary">
        Active Rides
      </h1>

      {/* Role Filter */}
      <div className="flex mb-4">
        <div className="flex items-center space-x-2 mr-4">
          <span className="text-gray-700 font-medium">Filter by Role:</span>
          <div className="flex space-x-1">
            <button
              className={`px-4 py-2 rounded-md ${selectedRole === "awaiting-rides"
                ? "bg-celadon text-primary"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              onClick={() => handleRoleChange("awaiting-rides")}
            >
              Awaiting Rides
            </button>
            <button
              className={`px-4 py-2 rounded-md ${selectedRole === "in-progress"
                ? "bg-celadon text-primary"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              onClick={() => handleRoleChange("in-progress")}
            >
              In Progress
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        data && (
          <div className="">
            <div>
              <Table columns={columns} data={data} />
            </div>

            <AssignDriverModal
              isOpen={selectedRide ? true : false}
              onRequestClose={closeModal}
              ride={selectedRide}
              onVerify={onVerify}
              isAssigning={isAssigning}
            />
          </div>
        )
      )}
    </div>
  );
};

export default ActiveRides;

